import { DeleteOutlined, EditOutlined } from "@ant-design/icons"
import { Button, message, Modal, Space, Table, TableColumnsType, TablePaginationConfig, TableProps } from "antd"
import { useEffect } from "react"
import { useForm, useWatch } from "react-hook-form"
import { useNavigate } from "react-router"
import { t } from "~/languages/i18n"
import { FormValuesRecruitment, RecruitmentData } from "~/types/types"
import { deleteRecruitment, fetchRecruitments } from "../services/recruitment"

interface RecruitmentFilterProps {
  searchTerm: string
  key: number
}

const RecruitmentList = ({ searchTerm, key }: RecruitmentFilterProps) => {
  const { control, setValue } = useForm<FormValuesRecruitment>({
    defaultValues: {
      pagination: {
        page: 1,
        pageSize: 5,
        current: 1,
        total: 0,
      },
    },
  })

  const dataItem = useWatch({ control, name: "dataItem" })
  const loading = useWatch({ control, name: "loading" })
  const pagination = useWatch({ control, name: "pagination" })
  const navigate = useNavigate()

  const fetchRecruitmentData = async (paginate?: typeof pagination, searchTerm?: string) => {
    setValue("loading", true)
    try {
      const response = await fetchRecruitments(searchTerm)
      if (response?.error) {
        setValue("error", response.error.message)
        return
      }
      setValue("dataItem", response?.data || [])
      setValue("pagination", {
        ...pagination,
        total: response?.meta?.pagination.total ?? 0,
        current: response?.meta?.pagination.page ?? 1,
      })
    } catch (error) {
    } finally {
      setValue("loading", false)
    }
  }

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: t("Confirm Delete"),
      content: t("Are you sure you want to delete this recruitment?"),
      okText: t("Delete"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          setValue("loading", true)
          await deleteRecruitment(id)
          setValue(
            "dataItem",
            dataItem.filter(item => item.id !== id),
          )
          fetchRecruitmentData()
        } catch {
          message.error(t("Error deleting recruitment"))
        } finally {
          setValue("loading", false)
        }
      },
    })
  }

  const onChange: TableProps<RecruitmentData>["onChange"] = (pagination: TablePaginationConfig) => {
    const newPagination = {
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 5,
      total: pagination.total || 0,
      page: pagination.current || 1,
    }

    setValue("pagination", newPagination)
    fetchRecruitmentData(newPagination, searchTerm)
  }

  useEffect(() => {
    fetchRecruitmentData()
  }, [key, searchTerm])

  const columns: TableColumnsType<RecruitmentData> = [
    {
      title: t("Title"),
      dataIndex: ["attributes", "Title"],
      key: "title",
      width: "30%",
      sorter: (a, b) => a.attributes.Title.localeCompare(b.attributes.Title),
    },
    { title: t("Department"), dataIndex: ["attributes", "Department"], key: "department", width: "15%" },
    { title: t("Rank"), dataIndex: ["attributes", "Rank"], key: "rank", width: "10%" },
    { title: t("Quantity"), dataIndex: ["attributes", "Quantity"], key: "quantity", width: "7%" },
    {
      title: t("Form"),
      dataIndex: "Form",
      key: "form",
      width: "10%",
      render: form => (form ? t("Full-time") : t("Part-time")),
    },
    {
      title: t("Action"),
      key: "action",
      width: "15%",
      render: (_, record) => (
        <Space size="middle">
          <Button type="primary" icon={<EditOutlined />} onClick={() => navigate(`/recruitment/${record.id}`)}>
            {t("Edit")}
          </Button>
          <Button danger type="primary" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
            {t("Delete")}
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={dataItem}
      loading={loading}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
      }}
      onChange={onChange}
      scroll={{ x: 1500, y: 500 }}
    />
  )
}

export default RecruitmentList
